import DynamicImage from '@/components/commons/dynamic-image';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import { BasePromotionRewardProduct } from '@/types/http/promotion/common';
import { CircleCheck } from 'lucide-react';
import { Dispatch, SetStateAction } from 'react';

type CardGiftProductProps = {
  product: BasePromotionRewardProduct;
  selectedProducts: BaseFreeGift[];
  setSelectedTab: Dispatch<SetStateAction<'product_group' | 'product_item'>>;
  setSelectedProduct: Dispatch<SetStateAction<BasePromotionRewardProduct | undefined>>;
  colorScheme?: ThemeColorSchemeType;
};

export default function CardGiftProduct({
  product,
  selectedProducts,
  setSelectedTab,
  setSelectedProduct,
  colorScheme,
}: CardGiftProductProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3.5 rounded p-1',
        product.stock === 0 ? 'cursor-not-allowed' : 'cursor-pointer'
      )}
      role='button'
      tabIndex={product.stock === 0 ? -1 : 0}
      aria-disabled={product.stock === 0}
      onClick={() => {
        if (product.stock === 0) return;
        setSelectedProduct(product);
        setSelectedTab('product_item');
      }}
      style={
        {
          '--text-primary': colorScheme?.text || '#58C151',
          '--text-secondary': colorScheme?.textSecondary || '#6B7280',
        } as React.CSSProperties
      }
    >
      <div className='flex items-center gap-3.5'>
        <DynamicImage
          src={product.product_assets?.[0] ?? ''}
          alt='Product Image'
          width={80}
          height={80}
          className={cn(
            'aspect-square size-[70px] rounded-sm object-cover',
            product.stock === 0 && 'grayscale'
          )}
          sizes='100vw'
          draggable={false}
        />

        <div className='flex flex-col text-xs'>
          {product.stock === 0 && (
            <div className='w-auto'>
              <Badge variant='secondary' className='p-1 py-0.5 text-[10px] font-normal'>
                Sold Out
              </Badge>
            </div>
          )}
          <span
            className={cn(
              'line-clamp-2 font-bold',
              product.stock === 0
                ? 'text-gray-400'
                : isEmpty(colorScheme)
                ? 'text-[#58C151]'
                : 'text-[var(--text-primary)]'
            )}
          >
            {product.product_name}
          </span>
          <div className='flex items-center gap-1 font-medium'>
            <p className={cn(product.stock === 0 ? 'text-gray-400' : 'text-[--text-secondary]')}>
              {product.total_variants} variants
            </p>
          </div>
        </div>
      </div>

      {selectedProducts.some(
        (g) => g.product_group.product_group_id === product.product_group_id
      ) && (
        <Checkbox
          checked={selectedProducts.some(
            (g) => g.product_group.product_group_id === product.product_group_id
          )}
          className='mt-1 size-5 rounded-full border-2 data-[state=checked]:border-[var(--text-primary)] data-[state=checked]:bg-[var(--text-primary)] data-[state=checked]:text-white'
          isCustom
        >
          <CircleCheck className='w-6 h-6 text-red-500' />
        </Checkbox>
      )}
    </div>
  );
}
